import { Component, OnInit, OnDestroy } from '@angular/core';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import * as _ from 'underscore';

import { Subscription } from 'rxjs';
import { SitesService } from 'src/app/sites/sites.service';
import { UsersService } from 'src/app/users/users.service';
import { AdminService } from 'src/app/admin/admin.service';

@Component({
	selector: 'app-sites-map',
	templateUrl: './sites-map.component.html',
	styleUrls: ['./sites-map.component.css']
})
export class SitesMapComponent implements OnInit, OnDestroy {

	tags = [];
	data = {};
	sites = [];
	verifiedSites = [];
	allSites = [];
	filteredSites = [];
	expandedCustomersSub: Subscription = new Subscription();
	dataSub: Subscription = new Subscription();
	filteredSitesSub: Subscription = new Subscription();
	deviceFilterType: Subscription = new Subscription();
	sitesAnalytics: any;
	deviceTypeFilterSelected: string[] = [];
	initLat: any;
	initLong: any;
	activeDataSub: any;
	allSiteIds: number[] = [];
	isPreparingAnalytics = false;
	pendingFilteredSitesData: any = null;

	// Analytics-source comparison diagnostic: the new tables, below the main (top) info box. This one
	// forces the new source rather than following the selected one, so the two boxes read as old vs
	// new while old is the selected source.
	newTableAnalytics: any;
	newTableSub: Subscription = new Subscription();

	// Which source the top info box - the one every user sees - is actually served from.
	activeAnalyticsSource: string;

	constructor(
		private sideMenuService: SideMenuService,
		public sitesService: SitesService,
		public usersService: UsersService,
		private adminService: AdminService,
	) { }

	ngOnInit() {
		this.initLat = this.sitesService.defaultMapLocation.latitude;
		this.initLong = this.sitesService.defaultMapLocation.longitude;

		if (this.usersService.hasAccessFunction('analytics_source_comparison'))
			this.adminService.getAnalyticsSource().subscribe((res: any) => this.activeAnalyticsSource = res?.source);

		this.reset(true);

		this.prepareData();
		this.dataSub = this.sideMenuService.data.subscribe(data => {
			this.data = data;
			this.sites = [];
			_.pluck(data, "sites").forEach(item=>{
				this.sites = this.sites.concat(item);
			});
			this.allSites = this.sites;
			this.allSiteIds = this.allSites.map(site => site.id); // cache all IDs
			this.filterVerifiedSites();
			this.prepareSitesAnalytics();
		});

		this.deviceFilterType = this.sideMenuService.showInfoRelatedDevicesType.subscribe((data: any[]) => {
			this.deviceTypeFilterSelected = data;
		});

		this.expandedCustomersSub = this.sideMenuService.expandedCustomers.subscribe(data => {
			let sites = [];
			this.tags = data;

			_.pluck(data, "sites").forEach(item=>{
				sites = sites.concat(item);
			});

			this.sites = sites;
			let all = false;

			if (this.tags.length == 0 && this.filteredSites.length ==0)
				this.sites = this.allSites;

			if (!data.length)
				all = true;

			this.filterVerifiedSites(all);
		});

		this.filteredSitesSub = this.sideMenuService.filteredSites.subscribe(data => {
			if (this.isPreparingAnalytics) {

				this.pendingFilteredSitesData = data;

				return;
			}

			this.handleFilteredSites(data);
		});
	}

	handleFilteredSites(data) {
		this.sites = [];

		this.filteredSites = data;

		if (data.length > 0) {

			this.sites = data;

		} else {

			this.sites = this.allSites;
		}

		this.filterVerifiedSites();

		this.prepareSitesAnalytics();
	}

	closeExpandedCustomer(customerId) {
		this.sideMenuService.removeExpandedCustomer(customerId);
	}

	reset(withResetFilters = false) {
		if(withResetFilters)
			this.sideMenuService.setSideMenuFilters({ showSitesWithStudies: 'devices_with_studies_sites' });

		this.sideMenuService.setSitesMapClickedSite({});
		this.sideMenuService.setExpandedCustomers([]);
		this.sideMenuService.setCurrentSite({});
		this.sideMenuService.setCurrentCustomer({});
	}

	ngOnDestroy() {
		this.reset();
		this.expandedCustomersSub.unsubscribe();
		this.dataSub.unsubscribe();
		this.filteredSitesSub.unsubscribe();
		this.deviceFilterType.unsubscribe();
		this.activeDataSub.unsubscribe();
		this.newTableSub.unsubscribe();

		this.sideMenuService.setRouteHasSiteInfoBox(false);
	}

	prepareSitesAnalytics() {
		if (this.isPreparingAnalytics) 
			return;

		this.isPreparingAnalytics = true;

		const siteIds = this.filteredSites.length > 0 ? this.filteredSites.map(site => site.id) : this.allSiteIds;

		if (siteIds.length === 0) {
			this.isPreparingAnalytics = false;
			return;
		}

		this.sitesService.getSitesAnalyticsDataCached(siteIds);

		if (this.usersService.hasAccessFunction('analytics_source_comparison'))
			this.loadTableComparison(siteIds);
	}

	// Analytics-source comparison diagnostic: see comment on newTableAnalytics above.
	loadTableComparison(siteIds: number[]) {
		this.newTableSub.unsubscribe();
		this.newTableSub = this.sitesService.getSitesAnalyticsDataForSource(siteIds, 'new').subscribe(sitesAnalytics => {
			this.newTableAnalytics = this.sumSiteAnalytics(sitesAnalytics);
		});
	}

	filterVerifiedSites(all: boolean = false) {
		const sites = all ? this.allSites : this.sites;

		this.verifiedSites = _.where(sites, {is_verified: true});
	}

	prepareData() {
		this.activeDataSub = this.sitesService.activeSiteAnalyticsData.subscribe(sitesAnalytics => {
			this.sitesAnalytics = this.sumSiteAnalytics(sitesAnalytics);

			this.isPreparingAnalytics = false;

			if (this.pendingFilteredSitesData) {

				const pending = this.pendingFilteredSitesData;

				this.pendingFilteredSitesData = null;

				this.handleFilteredSites(pending);
			}
		});
	}

	private sumSiteAnalytics(sitesAnalytics) {
		const result = {
			ioTAhDevices: {
				hours: 0,
				ahrs: 0,
				whrs: 0,
				count: 0,
				billed_whrs: 0
			},
			chargLinkDevices: {
				hours: 0,
				ahrs: 0,
				whrs: 0,
				count: 0,
				billed_whrs: 0
			},
			battLinkDevices: {
				count: 0,
				battery_inuse_ahrs: 0,
				battery_inuse_duration: 0,
				battery_charge_ahrs: 0
			},
			iotemeterDevices: {
				count: 0,
				total_consumed_energy: 0,
				current_year_consumed_energy: 0
			},
		};
		for (const siteAnalytics of sitesAnalytics) {
			result.ioTAhDevices.hours += (siteAnalytics.ioTAh_devices?.hours || 0);
			result.ioTAhDevices.ahrs += (siteAnalytics.ioTAh_devices?.ahrs || 0);
			result.ioTAhDevices.whrs += (siteAnalytics.ioTAh_devices?.whrs || 0);
			result.ioTAhDevices.count += (siteAnalytics.ioTAh_devices?.count || 0);
			result.ioTAhDevices.billed_whrs += (siteAnalytics.ioTAh_devices?.billed_whrs || 0);

			result.chargLinkDevices.hours += (siteAnalytics.chargLink_devices?.hours || 0);
			result.chargLinkDevices.ahrs += (siteAnalytics.chargLink_devices?.ahrs || 0);
			result.chargLinkDevices.whrs += (siteAnalytics.chargLink_devices?.whrs || 0);
			result.chargLinkDevices.count += (siteAnalytics.chargLink_devices?.count || 0);
			result.chargLinkDevices.billed_whrs += (siteAnalytics.chargLink_devices?.billed_whrs || 0);

			result.battLinkDevices.count += (siteAnalytics.battLink_devices?.count || 0);
			result.battLinkDevices.battery_inuse_duration += (siteAnalytics.battLink_devices?.battery_inuse_duration || 0);
			result.battLinkDevices.battery_charge_ahrs += (siteAnalytics.battLink_devices?.battery_charge_ahrs || 0);
			result.battLinkDevices.battery_inuse_ahrs += (siteAnalytics.battLink_devices?.battery_inuse_ahrs || 0)

			result.iotemeterDevices.count += (siteAnalytics.iotemeter_devices?.count || 0);
			result.iotemeterDevices.total_consumed_energy += (siteAnalytics.iotemeter_devices?.total_consumed_energy || 0);
			result.iotemeterDevices.current_year_consumed_energy += (siteAnalytics.iotemeter_devices?.current_year_consumed_energy || 0);
		}
		return result;
	}
}
